import { useState, useEffect } from 'react'
import { Download, Users, TrendingUp, Target, Activity, Copy, Check } from 'lucide-react'
import SessionViewer from '../components/SessionViewer'
import ParticipantsListViewer from '../components/ParticipantsListViewer'

// Format date to Eastern Time (automatically handles EST/EDT)
function formatEasternTime(dateString) {
  if (!dateString) return 'N/A'
  
  // Database stores UTC timestamps without timezone indicator
  // Append 'Z' to force UTC interpretation before converting to Eastern time
  const utcString = dateString.includes('Z') || dateString.includes('+') 
    ? dateString 
    : dateString.replace(' ', 'T') + 'Z'
  const date = new Date(utcString)
  
  // Get formatted date with timezone
  const formatted = date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short' // This will show EST or EDT automatically
  })
  
  return formatted
}

export default function ResearchDashboard() {
  const [loading, setLoading] = useState(true)
  const [summaryData, setSummaryData] = useState(null)
  const [participantData, setParticipantData] = useState(null)
  const [sessionData, setSessionData] = useState(null)
  const [iterationData, setIterationData] = useState(null)
  const [activeTab, setActiveTab] = useState('participants-list')
  const [error, setError] = useState(null)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const fetchJSON = async (url, label) => {
    const res = await fetch(url)
    let data = null
    try {
      data = await res.json()
    } catch (e) {
      throw new Error(`${label} response was not valid JSON (${res.status})`)
    }

    if (!res.ok || (data && data.error)) {
      const message = data && data.error ? data.error : `${label} load failed (${res.status})`
      throw new Error(message)
    }

    return data
  }

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Load all data types in parallel
      const [summary, participants, sessions, iterations] = await Promise.all([
        fetchJSON('/api/research-data?type=summary', 'Summary'),
        fetchJSON('/api/research-data?type=participants', 'Participants'),
        fetchJSON('/api/research-data?type=sessions', 'Sessions'),
        fetchJSON('/api/research-data?type=iterations', 'Iterations')
      ])
      
      setSummaryData(summary)
      setParticipantData(participants)
      setSessionData(sessions)
      setIterationData(iterations)
    } catch (err) {
      console.error('Dashboard load error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const downloadCSV = async (type) => {
    try {
      const response = await fetch(`/api/research-data?format=csv&type=${type}`)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${type}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      alert(`Export failed: ${err.message}`)
    }
  }

  const closeStaleSessions = async () => {
    const maxAge = prompt('Close sessions older than (minutes):', '60')
    if (!maxAge) return
    
    try {
      const response = await fetch('/api/research-close-stale-sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxAgeMinutes: parseInt(maxAge) })
      })
      
      const result = await response.json()
      
      if (result.closed > 0) {
        alert(`Closed ${result.closed} stale sessions. Reloading data...`)
        loadDashboardData() // Reload to show updated data
      } else {
        alert('No stale sessions found.')
      }
    } catch (err) {
      alert(`Failed to close sessions: ${err.message}`)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-p5-dark text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-p5-pink mx-auto mb-4"></div>
          <p className="text-gray-400">Loading research data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-p5-dark text-white flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-red-400 mb-4">Error loading dashboard: {error}</p>
          <button 
            onClick={loadDashboardData}
            className="px-4 py-2 bg-p5-pink text-white rounded hover:bg-pink-600"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen bg-p5-dark text-white overflow-y-auto">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-p5-pink mb-1">
              Research Dashboard
            </h1>
            <p className="text-gray-400 text-sm">
              Dyslexia & AI Coding Research - p5.js AI Studio
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => downloadCSV('participants')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded"
            >
              <Download size={16} />
              Participants CSV
            </button>
            <button
              onClick={() => downloadCSV('sessions')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded"
            >
              <Download size={16} />
              Sessions CSV
            </button>
            <button
              onClick={() => downloadCSV('iterations')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded"
            >
              <Download size={16} />
              Iterations CSV
            </button>
            <button
              onClick={closeStaleSessions}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 rounded ml-4"
            >
              Close Stale Sessions
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex space-x-1">
            {['participants-list', 'overview', 'participants', 'sessions', 'iterations'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 border-b-2 transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-p5-pink text-p5-pink'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                {tab === 'participants-list' ? 'All Participants' : tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div id="main-content" className={activeTab === 'participants-list' ? '' : 'max-w-7xl mx-auto px-6 py-8'}>
        {activeTab === 'participants-list' && (
          <ParticipantsListViewer />
        )}
        {activeTab === 'overview' && summaryData && (
          <OverviewTab data={summaryData} />
        )}
        {activeTab === 'participants' && participantData && (
          <ParticipantsTab data={participantData} />
        )}
        {activeTab === 'sessions' && sessionData && (
          <SessionsTab data={sessionData} />
        )}
        {activeTab === 'iterations' && iterationData && (
          <IterationsTab data={iterationData} />
        )}
      </div>
    </div>
  )
}

function OverviewTab({ data }) {
  const { overview } = data
  
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4">Overview Statistics</h2>
      
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          icon={<Users size={24} />}
          label="Total Participants"
          value={overview.totalParticipants}
          subtitle={`${overview.dyslexicParticipants} dyslexic, ${overview.nonDyslexicParticipants} non-dyslexic`}
        />
        <MetricCard
          icon={<Activity size={24} />}
          label="Total Sessions"
          value={overview.totalSessions}
          subtitle={`Avg ${overview.avgIterationsPerSession.toFixed(1)} iterations/session`}
        />
        <MetricCard
          icon={<TrendingUp size={24} />}
          label="Total Interactions"
          value={overview.totalIterations}
        />
        <MetricCard
          icon={<Target size={24} />}
          label="Success Rate"
          value={`${overview.overallSuccessRate.toFixed(1)}%`}
          subtitle="Applied code or thumbs up"
        />
      </div>
      
      {/* Summary Text */}
      <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
        <h3 className="text-lg font-semibold mb-4 text-p5-pink">Research Progress</h3>
        <div className="space-y-3 text-gray-300">
          <p>
            <strong className="text-white">Participant Split:</strong>{' '}
            {overview.totalParticipants > 0
              ? `${((overview.dyslexicParticipants / overview.totalParticipants) * 100).toFixed(1)}% dyslexic, ${((overview.nonDyslexicParticipants / overview.totalParticipants) * 100).toFixed(1)}% non-dyslexic`
              : 'No participants yet'}
          </p>
          <p>
            <strong className="text-white">Data Collection:</strong>{' '}
            {overview.totalIterations} AI interactions across {overview.totalSessions} sessions
          </p>
          <p>
            <strong className="text-white">Engagement:</strong>{' '}
            Average of {overview.avgIterationsPerSession.toFixed(1)} interactions per session
          </p>
          <p>
            <strong className="text-white">Quality:</strong>{' '}
            {overview.overallSuccessRate.toFixed(1)}% of interactions resulted in applied code or positive feedback
          </p>
        </div>
      </div>
    </div>
  )
}

function ParticipantsTab({ data }) {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4">Participant Demographics</h2>
      
      {/* Coding Experience */}
      {data.codingExperience && data.codingExperience.length > 0 && (
        <DataTable
          title="Coding Experience"
          headers={['Experience Level', 'Dyslexic', 'Non-Dyslexic', 'Unknown', 'Total']}
          rows={groupByCodingExperience(data.codingExperience)}
        />
      )}
      
      {/* Challenges (Dyslexic only) */}
      {data.challenges && data.challenges.length > 0 && (
        <DataTable
          title="Main Challenges (Dyslexic Participants)"
          headers={['Challenge', 'Count', 'Percentage']}
          rows={formatChallenges(data.challenges)}
        />
      )}
    </div>
  )
}

function SessionsTab({ data }) {
  const [selectedSession, setSelectedSession] = useState(null)
  const [sessionDetails, setSessionDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)

  const handleSessionClick = async (sessionId) => {
    setLoadingDetails(true)
    try {
      const response = await fetch(`/api/research-data?type=session-details&sessionId=${sessionId}`)
      
      // Check if response is OK before parsing
      if (!response.ok) {
        throw new Error(`Failed to load session: ${response.status} ${response.statusText}`)
      }
      
      const details = await response.json()
      
      // Check if API returned an error object
      if (details.error) {
        throw new Error(details.error)
      }
      
      setSessionDetails(details)
      setSelectedSession(sessionId)
    } catch (error) {
      console.error('Failed to load session details:', error)
      alert(`Could not load session details: ${error.message}`)
    } finally {
      setLoadingDetails(false)
    }
  }

  if (selectedSession && sessionDetails) {
    return <SessionViewer 
      session={sessionDetails} 
      onBack={() => { setSelectedSession(null); setSessionDetails(null) }}
    />
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4">Session Analysis</h2>
      
      {/* Session Metrics Comparison */}
      {data.sessionMetrics && data.sessionMetrics.length > 0 && (
        <ComparisonTable
          title="Session Metrics: Dyslexic vs Non-Dyslexic"
          data={data.sessionMetrics}
        />
      )}
      
      {/* Fallback: Raw Sessions (when metrics aren't computed yet) */}
      {data.rawSessions && data.rawSessions.length > 0 && (
        <ClickableSessionsTable
          sessions={data.rawSessions}
          onSessionClick={handleSessionClick}
          loading={loadingDetails}
        />
      )}
      
      {/* Prompt Patterns */}
      {data.promptPatterns && data.promptPatterns.length > 0 && (
        <DataTable
          title="Dominant Prompt Patterns"
          headers={['Pattern', 'Dyslexic', 'Non-Dyslexic', 'Unknown']}
          rows={groupByPattern(data.promptPatterns)}
        />
      )}
      
      {/* Iteration Patterns */}
      {data.iterationPatterns && data.iterationPatterns.length > 0 && (
        <DataTable
          title="Iteration Patterns"
          headers={['Pattern', 'Dyslexic', 'Non-Dyslexic', 'Unknown']}
          rows={groupByIterationPattern(data.iterationPatterns)}
        />
      )}
    </div>
  )
}

function IterationsTab({ data }) {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold mb-4">Iteration Analysis</h2>
      
      {/* Prompt Types */}
      {data.promptTypes && data.promptTypes.length > 0 && (
        <PromptTypesTable data={data.promptTypes} />
      )}
      
      {/* Code Changes */}
      {data.codeChanges && data.codeChanges.length > 0 && (
        <CodeChangesTable data={data.codeChanges} />
      )}
      
      {/* Code Snippet Usage */}
      {data.codeSnippetUsage && data.codeSnippetUsage.length > 0 && (
        <CodeSnippetTable data={data.codeSnippetUsage} />
      )}
    </div>
  )
}

// Helper Components

function MetricCard({ icon, label, value, subtitle }) {
  return (
    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
      <div className="flex items-start justify-between mb-2">
        <div className="text-p5-pink">{icon}</div>
      </div>
      <div className="text-3xl font-bold text-white mb-1">{value}</div>
      <div className="text-sm text-gray-400">{label}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  )
}

function DataTable({ title, headers, rows }) {
  return (
    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
      <h3 className="text-lg font-semibold mb-4 text-p5-pink">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              {headers.map((header, i) => (
                <th key={i} className="text-left py-3 px-4 text-gray-400 font-medium">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-gray-800 hover:bg-gray-800/50">
                {row.map((cell, j) => (
                  <td key={j} className="py-3 px-4 text-gray-300">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ComparisonTable({ title, data }) {
  const dyslexic = data.find(d => d.has_dyslexia === 1) || {}
  const nonDyslexic = data.find(d => d.has_dyslexia === 0) || {}
  
  const metrics = [
    { label: 'Avg Prompts per Session', key: 'avg_prompts' },
    { label: 'Avg Successful Prompts', key: 'avg_successful' },
    { label: 'Avg Session Duration (min)', key: 'avg_duration' },
    { label: 'Avg Prompt Length (chars)', key: 'avg_length' },
    { label: 'Avg Code Quality Score', key: 'avg_quality' },
    { label: 'Goal Achievement Rate (%)', key: 'goal_rate' },
    { label: 'Total Sessions', key: 'session_count' }
  ]
  
  return (
    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
      <h3 className="text-lg font-semibold mb-4 text-p5-pink">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Metric</th>
              <th className="text-right py-3 px-4 text-gray-400 font-medium">Dyslexic</th>
              <th className="text-right py-3 px-4 text-gray-400 font-medium">Non-Dyslexic</th>
              <th className="text-right py-3 px-4 text-gray-400 font-medium">Difference</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, i) => {
              const dysVal = dyslexic[metric.key] || 0
              const nonDysVal = nonDyslexic[metric.key] || 0
              const diff = dysVal - nonDysVal
              const diffPercent = nonDysVal !== 0 ? ((diff / nonDysVal) * 100) : 0
              
              return (
                <tr key={i} className="border-b border-gray-800 hover:bg-gray-800/50">
                  <td className="py-3 px-4 text-gray-300">{metric.label}</td>
                  <td className="py-3 px-4 text-right text-gray-300">
                    {typeof dysVal === 'number' ? dysVal.toFixed(2) : dysVal}
                  </td>
                  <td className="py-3 px-4 text-right text-gray-300">
                    {typeof nonDysVal === 'number' ? nonDysVal.toFixed(2) : nonDysVal}
                  </td>
                  <td className={`py-3 px-4 text-right ${
                    diff > 0 ? 'text-green-400' : diff < 0 ? 'text-red-400' : 'text-gray-400'
                  }`}>
                    {diff > 0 ? '+' : ''}{diff.toFixed(2)} ({diffPercent.toFixed(1)}%)
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PromptTypesTable({ data }) {
  const grouped = {}
  data.forEach(item => {
    if (!grouped[item.prompt_pattern]) {
      grouped[item.prompt_pattern] = { dyslexic: null, nonDyslexic: null }
    }
    if (item.has_dyslexia === 1) {
      grouped[item.prompt_pattern].dyslexic = item
    } else {
      grouped[item.prompt_pattern].nonDyslexic = item
    }
  })
  
  const rows = Object.entries(grouped).map(([pattern, values]) => {
    const dysCount = values.dyslexic?.count || 0
    const dysSuccess = (values.dyslexic?.success_rate || 0) * 100
    const nonDysCount = values.nonDyslexic?.count || 0
    const nonDysSuccess = (values.nonDyslexic?.success_rate || 0) * 100
    
    return [
      pattern,
      `${dysCount} (${dysSuccess.toFixed(1)}% success)`,
      `${nonDysCount} (${nonDysSuccess.toFixed(1)}% success)`
    ]
  })
  
  return (
    <DataTable
      title="Prompt Types & Success Rates"
      headers={['Prompt Type', 'Dyslexic (count, success%)', 'Non-Dyslexic (count, success%)']}
      rows={rows}
    />
  )
}

function CodeChangesTable({ data }) {
  const rows = data.map(item => [
    item.has_dyslexia === 1 ? 'Dyslexic' : 'Non-Dyslexic',
    item.avg_lines_added?.toFixed(1) || '0',
    item.avg_lines_removed?.toFixed(1) || '0',
    item.avg_prompt_length?.toFixed(0) || '0',
    item.avg_word_count?.toFixed(1) || '0'
  ])
  
  return (
    <DataTable
      title="Code Change Patterns"
      headers={['Group', 'Avg Lines Added', 'Avg Lines Removed', 'Avg Prompt Length', 'Avg Word Count']}
      rows={rows}
    />
  )
}

function CodeSnippetTable({ data }) {
  const grouped = {}
  data.forEach(item => {
    const key = item.has_code_snippet ? 'With Code Snippet' : 'Without Code Snippet'
    if (!grouped[key]) {
      grouped[key] = { dyslexic: null, nonDyslexic: null }
    }
    if (item.has_dyslexia === 1) {
      grouped[key].dyslexic = item
    } else {
      grouped[key].nonDyslexic = item
    }
  })
  
  const rows = Object.entries(grouped).map(([type, values]) => {
    const dysCount = values.dyslexic?.count || 0
    const dysSuccess = (values.dyslexic?.success_rate || 0) * 100
    const nonDysCount = values.nonDyslexic?.count || 0
    const nonDysSuccess = (values.nonDyslexic?.success_rate || 0) * 100
    
    return [
      type,
      `${dysCount} (${dysSuccess.toFixed(1)}% success)`,
      `${nonDysCount} (${nonDysSuccess.toFixed(1)}% success)`
    ]
  })
  
  return (
    <DataTable
      title="Code Snippet Usage in Prompts"
      headers={['Type', 'Dyslexic (count, success%)', 'Non-Dyslexic (count, success%)']}
      rows={rows}
    />
  )
}

// Data formatting helpers

function groupByCodingExperience(data) {
  const grouped = {}
  data.forEach(item => {
    if (!grouped[item.coding_experience]) {
      grouped[item.coding_experience] = { dyslexic: 0, nonDyslexic: 0, unknown: 0 }
    }
    if (item.has_dyslexia === 1) {
      grouped[item.coding_experience].dyslexic = item.count
    } else if (item.has_dyslexia === 0) {
      grouped[item.coding_experience].nonDyslexic = item.count
    } else {
      grouped[item.coding_experience].unknown = item.count
    }
  })
  
  return Object.entries(grouped).map(([exp, counts]) => [
    exp || 'unknown',
    counts.dyslexic,
    counts.nonDyslexic,
    counts.unknown,
    counts.dyslexic + counts.nonDyslexic + counts.unknown
  ])
}

function formatChallenges(data) {
  const total = data.reduce((sum, item) => sum + item.count, 0)
  return data.map(item => [
    item.main_challenges,
    item.count,
    `${((item.count / total) * 100).toFixed(1)}%`
  ])
}

function groupByPattern(data) {
  const grouped = {}
  data.forEach(item => {
    if (!grouped[item.prompt_style]) {
      grouped[item.prompt_style] = { dyslexic: 0, nonDyslexic: 0, unknown: 0 }
    }
    if (item.has_dyslexia === 1) {
      grouped[item.prompt_style].dyslexic = item.count
    } else if (item.has_dyslexia === 0) {
      grouped[item.prompt_style].nonDyslexic = item.count
    } else {
      grouped[item.prompt_style].unknown = item.count
    }
  })
  
  return Object.entries(grouped).map(([pattern, counts]) => [
    pattern || 'unknown',
    counts.dyslexic,
    counts.nonDyslexic,
    counts.unknown
  ])
}

function groupByIterationPattern(data) {
  const grouped = {}
  data.forEach(item => {
    if (!grouped[item.iteration_pattern]) {
      grouped[item.iteration_pattern] = { dyslexic: 0, nonDyslexic: 0, unknown: 0 }
    }
    if (item.has_dyslexia === 1) {
      grouped[item.iteration_pattern].dyslexic = item.count
    } else if (item.has_dyslexia === 0) {
      grouped[item.iteration_pattern].nonDyslexic = item.count
    } else {
      grouped[item.iteration_pattern].unknown = item.count
    }
  })
  
  return Object.entries(grouped).map(([pattern, counts]) => [
    pattern,
    counts.dyslexic,
    counts.nonDyslexic,
    counts.unknown
  ])
}

function ClickableSessionsTable({ sessions, onSessionClick, loading }) {
  const [copiedId, setCopiedId] = useState(null)
  
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }
  
  return (
    <div className="bg-gray-900 rounded-lg p-6 border border-gray-800">
      <h3 className="text-lg font-semibold mb-4 text-p5-pink">Sessions (Click to view conversation)</h3>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Session ID</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Participant Type</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Started</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Ended</th>
              <th className="text-left py-3 px-4 text-gray-400 font-medium">Iterations</th>
            </tr>
          </thead>
          <tbody>
            {sessions.map(session => (
              <tr
                key={session.id}
                onClick={() => onSessionClick(session.id)}
                className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer transition-colors"
              >
                <td className="py-3 px-4 text-gray-300 font-mono text-sm">
                  <div className="flex items-center gap-2">
                    <span>{session.id.substring(0, 8)}...</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        copyToClipboard(session.id, session.id)
                      }}
                      className="p-1 hover:bg-gray-700 rounded transition-colors"
                      title="Copy full session ID"
                    >
                      {copiedId === session.id ? (
                        <Check size={12} className="text-green-400" />
                      ) : (
                        <Copy size={12} className="text-gray-400" />
                      )}
                    </button>
                  </div>
                </td>
                <td className="py-3 px-4 text-gray-300">
                  <span className={`px-2 py-1 rounded text-xs ${
                    ['ai_llm', 'ai_llm_bot'].includes(session.dyslexic_status) || session.participant_type === 'ai_bot'
                      ? 'bg-purple-500/20 text-purple-300'
                      : ['yes', 'yes_special_school', 'suspected'].includes(session.dyslexic_status) 
                        ? 'bg-blue-500/20 text-blue-300' 
                        : session.dyslexic_status === 'no'
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-yellow-500/20 text-yellow-300'
                  }`}>
                    {['ai_llm', 'ai_llm_bot'].includes(session.dyslexic_status) || session.participant_type === 'ai_bot'
                      ? '🤖 AI/LLM Bot'
                      : ['yes', 'yes_special_school', 'suspected'].includes(session.dyslexic_status) 
                        ? '🧠 Dyslexic' 
                        : session.dyslexic_status === 'no'
                          ? '✅ Non-Dyslexic'
                          : '❓ Unknown'}
                  </span>
                </td>
                <td className="py-3 px-4 text-gray-300 text-sm">{formatEasternTime(session.started_at)}</td>
                <td className="py-3 px-4 text-gray-300 text-sm">{session.ended_at ? formatEasternTime(session.ended_at) : '🟢 Active'}</td>
                <td className="py-3 px-4 text-gray-300">{session.iteration_count || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="text-center py-4 text-gray-400">Loading session details...</div>}
      </div>
    </div>
  )
}

// SessionDetailView removed - SessionsTab uses SessionViewer directly
